import axios from 'axios';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/lib/AuthContext';
import { usePermissions } from '@/lib/PermissionsContext';
import { Users, Clock, CalendarDays, ArrowRight, AlertTriangle, CheckCircle, Eye, Lightbulb, TrendingUp } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_CONFIG = {
  en_bonne_voie: { label: 'En bonne voie', color: 'bg-green-100 text-green-800', border: 'border-green-200', bg: 'bg-green-50/20', progressColor: 'bg-green-500' },
  a_surveiller: { label: 'A surveiller', color: 'bg-amber-100 text-amber-800', border: 'border-amber-200', bg: 'bg-amber-50/20', progressColor: 'bg-amber-500' },
  en_retard_probable: { label: 'En retard probable', color: 'bg-red-100 text-red-800', border: 'border-red-200', bg: 'bg-red-50/20', progressColor: 'bg-red-500' },
};

function AvancementBar({ bon }) {
  const pctTemps = bon.pct_temps || 0;
  const pctHeures = bon.pct_heures || 0;
  const cfg = STATUS_CONFIG[bon.statut_avancement] || STATUS_CONFIG.en_bonne_voie;

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <span className="text-[10px] text-slate-400 w-12 shrink-0">Temps</span>
        <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full bg-slate-400 rounded-full transition-all" style={{ width: `${Math.min(100, pctTemps)}%` }} />
        </div>
        <span className="text-[10px] text-slate-500 w-12 text-right">J{bon.jours_passes}/{bon.total_jours}</span>
      </div>
      {bon.heures_budget > 0 && (
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400 w-12 shrink-0">Heures</span>
          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all ${cfg.progressColor}`} style={{ width: `${Math.min(100, pctHeures)}%` }} />
          </div>
          <span className="text-[10px] text-slate-500 w-12 text-right">{bon.heures_punchees}h/{bon.heures_budget}h</span>
        </div>
      )}
      {bon.pct_etapes !== null && bon.pct_etapes !== undefined && bon.etapes_total >= 3 && (
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-400 w-12 shrink-0">Etapes</span>
          <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-indigo-400 rounded-full transition-all" style={{ width: `${Math.min(100, bon.pct_etapes)}%` }} />
          </div>
          <span className="text-[10px] text-slate-500 w-12 text-right">{bon.etapes_faites}/{bon.etapes_total}</span>
        </div>
      )}
    </div>
  );
}

function PredictionBadge({ bon, isCP }) {
  const msg = isCP ? bon.prediction_cp : bon.prediction_equipe;
  if (!msg) return null;

  const isPositive = bon.prediction_jours_depassement <= 0;
  const bgClass = isPositive ? 'bg-emerald-50 border-emerald-200' : 'bg-orange-50 border-orange-200';
  const textClass = isPositive ? 'text-emerald-700' : 'text-orange-700';
  const Icon = isPositive ? TrendingUp : Lightbulb;

  return (
    <div className={`flex items-start gap-1.5 text-[10px] rounded-md px-2 py-1.5 border ${bgClass} ${textClass}`} data-testid="prediction-badge">
      <Icon className="w-3 h-3 mt-0.5 shrink-0" />
      <span>{msg}</span>
    </div>
  );
}

export function EquipesActives() {
  const [equipes, setEquipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedTeams, setExpandedTeams] = useState({}); // Pour gérer "Voir plus"
  const { user } = useAuth();
  const { canDo } = usePermissions() || {};

  // 🆕 [Phase v13.61 Vague 5b — 2026-08-25] Branchement RBAC via `equipes_actives_prediction_cp`.
  //   CP/owner voient un message factuel (« prédiction »), les autres voient l'encouragement.
  const isCP = !!(canDo && canDo('equipes_actives_prediction_cp'));

  const MAX_RETARDS_SHOWN = 2; // Limiter à 2 chantiers en retard affichés par défaut

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/crm/equipes-actives`);
        if (res.status === 200) {
          const data = res.data;
          setEquipes(data.equipes || []);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const toggleExpanded = (chefId) => {
    setExpandedTeams(prev => ({ ...prev, [chefId]: !prev[chefId] }));
  };

  if (loading) return null;
  if (equipes.length === 0) return null;

  return (
    <div data-testid="equipes-actives-section">
      <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3 flex items-center gap-2">
        <Users className="w-4 h-4 text-indigo-600" /> Equipes actives — Aujourd'hui
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {equipes.map(eq => {
          const enc = eq.chantier_en_cours;
          const cfg = enc ? (STATUS_CONFIG[enc.statut_avancement] || STATUS_CONFIG.en_bonne_voie) : null;

          return (
            <Card key={eq.chef_id} className={`border ${enc ? cfg.border + ' ' + cfg.bg : eq.en_retard?.length > 0 ? 'border-red-200 bg-red-50/20' : 'border-slate-200'}`} data-testid={`equipe-card-${eq.chef_id}`}>
              <CardHeader className="pb-2 pt-3 px-4">
                <CardTitle className="text-sm flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Users className="w-3.5 h-3.5 text-indigo-600" />
                    {eq.chef_nom}
                  </span>
                  {enc ? (
                    <Badge className={`text-[10px] ${cfg.color}`}>{cfg.label}</Badge>
                  ) : eq.en_retard?.length > 0 ? (
                    <Badge className="bg-red-100 text-red-800 text-[10px]">En retard</Badge>
                  ) : eq.prochains?.length > 0 ? (
                    <Badge className="bg-blue-100 text-blue-800 text-[10px]">Prochain bientot</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px]">Disponible</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 pb-3 space-y-2">
                {enc && (
                  <div className="bg-white rounded-lg border p-2.5 space-y-2">
                    <div className="flex items-center justify-between gap-1">
                      <p className="font-medium text-xs truncate">{enc.client_nom}</p>
                      <Badge variant="outline" className="text-[10px] shrink-0">
                        {enc.type_travaux === 'exterieur' ? 'Ext.' : 'Int.'}
                      </Badge>
                    </div>
                    <p className="text-[11px] text-slate-500 truncate">{enc.nom_dossier}</p>

                    <AvancementBar bon={enc} />

                    <div className="flex items-center justify-between text-[10px] text-slate-400 flex-wrap gap-1">
                      <span className="flex items-center gap-1">
                        <CalendarDays className="w-3 h-3" />
                        {enc.date_debut} <ArrowRight className="w-2.5 h-2.5" /> {enc.date_fin}
                      </span>
                      <span className="flex items-center gap-0.5">
                        <Users className="w-3 h-3" /> {enc.nb_employes} pers.
                      </span>
                    </div>

                    {/* Prédiction adaptée au rôle */}
                    <PredictionBadge bon={enc} isCP={isCP} />

                    {enc.lavage_pression_a_faire && !enc.lavage_pression_complete && (
                      <div className="flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 rounded px-1.5 py-0.5">
                        <CheckCircle className="w-3 h-3" /> Lavage a pression a faire
                      </div>
                    )}
                  </div>
                )}

                {eq.en_retard?.length > 0 && (() => {
                  const isExpanded = expandedTeams[eq.chef_id];
                  const retardsToShow = isExpanded ? eq.en_retard : eq.en_retard.slice(0, MAX_RETARDS_SHOWN);
                  const hasMore = eq.en_retard.length > MAX_RETARDS_SHOWN;

                  return (
                    <>
                      {retardsToShow.map(bon => (
                        <div key={bon.bon_id} className="bg-red-50 rounded-lg border border-red-200 p-2 space-y-1.5">
                          <div className="flex items-center gap-2">
                            <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                            <div className="min-w-0 flex-1">
                              <p className="text-[11px] font-medium text-red-800 truncate">{bon.client_nom} — {bon.nom_dossier}</p>
                              <p className="text-[10px] text-red-600">{bon.raison || `Devait finir le ${bon.date_fin}`}</p>
                              {bon.heures_budget > 0 && (
                                <p className="text-[10px] text-red-500">{bon.heures_punchees}h / {bon.heures_budget}h</p>
                              )}
                            </div>
                          </div>
                          <PredictionBadge bon={bon} isCP={isCP} />
                        </div>
                      ))}
                      {hasMore && (
                        <button
                          onClick={() => toggleExpanded(eq.chef_id)}
                          className="w-full text-xs text-red-600 hover:text-red-700 font-medium py-1 hover:bg-red-50 rounded transition-colors"
                        >
                          {isExpanded ? `Voir moins (−${eq.en_retard.length - MAX_RETARDS_SHOWN})` : `Voir ${eq.en_retard.length - MAX_RETARDS_SHOWN} de plus`}
                        </button>
                      )}
                    </>
                  );
                })()}

                {!enc && eq.prochains?.length > 0 && (
                  <div className="bg-blue-50/50 rounded-lg border border-blue-100 p-2">
                    <p className="text-[10px] text-blue-600 font-medium mb-1">Prochain chantier</p>
                    <p className="text-xs font-medium truncate">{eq.prochains[0].client_nom}</p>
                    <p className="text-[10px] text-slate-500">{eq.prochains[0].nom_dossier} — Debut {eq.prochains[0].date_debut}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
